// [檔案： src/main.js]

import './css/style.css'; 
import { initializeStorage } from './js/storage.js';
import { supabase } from './js/supabase.js';
import { showMessage } from './js/utils.js';
import { initializeTheme } from './js/theme.js';
import { initializeFontSize } from './js/font.js';
import { initializeCalendars } from './js/calendar.js';
import { initializeAuth } from './js/auth.js';
import { initializeTransactions, openAddTransactionModal } from './js/transactions.js';
import { initializeReports } from './js/reports.js';
import { dom, initializeDOM } from './js/dom.js'; 
import { initializeI18n } from './js/i18n.js';
import { initSwipeGestures } from './js/gestures.js';
import { initWidgetDeepLink } from './js/widget.js';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';
import { Capacitor } from '@capacitor/core';

// ✅ 設為 async
async function main() {
    try {
        // --- 鍵盤行為配置 (解決 iOS 背景推移與白邊問題) ---
        if (Capacitor.isNativePlatform()) {
            try {
                // ✅ 僅在原生 App 環境下啟用 bottom 過渡動畫 (避免 PC 版開啟彈窗時抖動)
                const modalEl = document.getElementById('add-tx-modal');
                if (modalEl) {
                    modalEl.style.transition = 'bottom 0.3s cubic-bezier(0.36, 0.66, 0.04, 1)';
                }

                // 1. 禁止 iOS 系統自動捲動 WebView (解決白邊)
                await Keyboard.setScroll({ isDisabled: true });

                // 2. 設定 Resize 模式為 None
                await Keyboard.setResizeMode({ mode: KeyboardResize.None });

                // 3. 監聽鍵盤事件：在卡片內部加 padding 讓內容上滾，卡片本身不動 (同時監聽 WillShow 與 DidShow 以完美相容 Android/三星裝置)
                const handleKeyboardShow = (info) => {
                    const modal = document.getElementById('add-tx-modal');
                    const modalBody = document.getElementById('add-tx-modal-body');
                    
                    if (modal && modal.style.display !== 'none' && modalBody) {
                        const kh = info ? (info.keyboardHeight || 0) : 0;
                        if (kh > 0) {
                            modalBody.style.paddingBottom = `${kh}px`;
                            modalBody.style.transition = 'padding-bottom 0.25s ease-out';

                            setTimeout(() => {
                                const activeEl = document.activeElement;
                                if (activeEl && modalBody.contains(activeEl) && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
                                    activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                }
                            }, 150);
                        }
                    }
                };

                const handleKeyboardHide = () => {
                    const modalBody = document.getElementById('add-tx-modal-body');
                    if (modalBody) {
                        modalBody.style.paddingBottom = '';
                    }
                };

                Keyboard.addListener('keyboardWillShow', handleKeyboardShow);
                Keyboard.addListener('keyboardDidShow', handleKeyboardShow);
                Keyboard.addListener('keyboardWillHide', handleKeyboardHide);
                Keyboard.addListener('keyboardDidHide', handleKeyboardHide);

                // 4. 當焦點在輸入框之間切換時，精確將目標輸入框平滑捲動至可見區域
                const modalBodyEl = document.getElementById('add-tx-modal-body');
                if (modalBodyEl) {
                    modalBodyEl.addEventListener('focusin', (e) => {
                        const target = e.target;
                        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                            // 延遲等待鍵盤/焦點穩定後，使用平滑 scrollIntoView 避免硬跳動
                            setTimeout(() => {
                                target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                            }, 150);
                        }
                    });
                }
            } catch (e) {
                console.warn('Keyboard plugin config failed:', e);
            }
        }

        // --- 鎖定背景高度 (防止手機鍵盤彈起時背景被推擠/壓縮) ---
        const bg = document.getElementById('app-background');
        if (bg) {
            bg.style.height = `${window.screen.height}px`;
            let lastWidth = window.innerWidth;
            window.addEventListener('resize', () => {
                if (window.innerWidth !== lastWidth) {
                    lastWidth = window.innerWidth;
                    bg.style.height = `${window.screen.height}px`;
                }
            });
        }
        // -----------------------------------------------------

        // 1. 初始化 DOM (最優先)
        initializeDOM(); 
        
        // 2. 並行執行基礎配置 (本地 I/O，通常很快)
        // 使用 Promise.all 讓 Storage, i18n, Theme, Font 同時跑，而不是排隊
        await Promise.all([
            initializeStorage(),
            initializeI18n(),
            initializeTheme(),
            initializeFontSize()
        ]);

        // 3. 初始化日曆 (依賴 i18n)
        await initializeCalendars(); 
        
        // 4. 初始化交易邏輯與事件綁定 (關鍵：提早執行以讓 UI 可交互)
        // 即使 Auth 還沒完成，按鈕已經可以點了 (雖然可能還沒數據)
        initializeTransactions();
        initializeReports();
        initSwipeGestures();
        initWidgetDeepLink(openAddTransactionModal);

        // 5. 初始化 Auth (依賴網路，可能最慢)
        // 把它放在事件綁定之後，避免網路請求阻塞 UI 互動
        await initializeAuth();
        
        function handleGlobalClick(e) {
            if (dom.txTypeBtn && !dom.txTypeBtn.contains(e.target) && !dom.txTypeOptions.contains(e.target)) {
                dom.txTypeOptions.classList.add('hidden');
            }
            if (dom.txCategoryBtn && !dom.txCategoryBtn.contains(e.target) && !dom.txCategoryOptions.contains(e.target)) {
                dom.txCategoryOptions.classList.add('hidden');
            }
        }
        document.addEventListener('click', handleGlobalClick);

        if (supabase.supabaseUrl.includes('YOUR_SUPABASE_URL')) {
            showMessage('Supabase 尚未配置，應用程式將僅在本機運作。');
        }
    } catch (error) {
        console.error("Failed to initialize the app:", error);
        alert(`應用程式啟動失敗 (App failed to initialize):\n\n${error.message}`);
    }
}

document.addEventListener('DOMContentLoaded', main);